import {
  ApprovalStatus,
  BookAppendixItemType,
  ExamResponseStatus,
  ExamType, FeatureCode,
  GameStatus,
  GameType,
  LectureStatus,
  LectureType, OrderState, PaymentChannel, ProductType,
  QuestionType,
  ThinkingLevel,
} from './enum';
import {BaseIdRequest, LearningAssetStatus, LearningBookStatus} from './request';

export type BaseIdWrapper = {
  id: string;
};

export type BaseResponse = {
  id: string;
  createdAt: Date;
  createdBy: string;
  lastModifiedAt: Date;
  lastModifiedBy: string;
  webViewActive: boolean;
  webViewOrder: number;
};

export type NameCodeResponse = BaseResponse & {
  id: string;
  name: string;
  code: string;
};

export interface Page<T> {
  content: T[];
  pageable: {
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    pageNumber: number;
    pageSize: number;
    paged: boolean;
    unpaged: boolean;
  };
  totalElements: number;
  totalPages: number;
  last: boolean;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

export type ErrorResponse = {
  errorCode: string;
  message: string;
};

export type RemoteResponseErrorType = {
  code: string;
  field: string;
  message: string;
};

export type RemoteResponseType<T> = {
  status: 'success' | 'failed';
  data: T;
  errors: null | RemoteResponseErrorType[];
  message: null | string;
};

export type Grade = NameCodeResponse & {};

export type TestBank = NameCodeResponse & {};

export type SchoolYear = NameCodeResponse & {};

export type Subject = NameCodeResponse & {};

export type SkillType = NameCodeResponse & {
  questionCount: number;
  subject: Subject;
  grade?: Grade;
};

export type BookAppendixBaseItem = BaseResponse & {
  bookAppendix: BaseIdWrapper;
  itemIndex: number;
  type: BookAppendixItemType;
  displayTitle: string;
  itemId: string;
};

export type BookAppendixExerciseFormat = BookAppendixBaseItem & {
  exerciseFormat: ExerciseFormat;
};

export type BookAppendixExam = BookAppendixBaseItem & {
  exam: Exam;
};

export type BookAppendixLecture = BookAppendixBaseItem & {
  lecture: Lecture;
};

export type BookAppendixTheory = BookAppendixBaseItem & {
  theory: Theory;
};

export type BookAppendixPractice = BookAppendixBaseItem & {
  practice: Exam;
};

export type BookAppendix = NameCodeResponse & {
  bookAppendixIndex: number;
  children: BookAppendix[];

  bookAppendixExerciseFormats: BookAppendixExerciseFormat[];
  bookAppendixExams: BookAppendixExam[];
  bookAppendixLectures: BookAppendixLecture[];
  bookAppendixTheories: BookAppendixTheory[];
  bookAppendixPractices: BookAppendixPractice[];
};

export type Book = NameCodeResponse & {
  publishCode?: string;
  link: string;
  thumbnail?: string;
  preface?: string;
  audio?: string;
  publishYear?: string;
  subject: Subject;
  testBank: TestBank;
  grades: Grade[];
};

export type QuestionDetail = BaseResponse & {
  code: string;
  description: string;
  image: string;
  questionDetailIndex: number;
};

export type Question = NameCodeResponse & {
  topic: string;
  rightAnswer?: string;
  type: QuestionType;
  pageNumber: number;
  thinkingLevels: ThinkingLevel[];
  hint: string;
  solutionGuide: string;
  image: string;
  imageSize: string;
  questionIndex: number;
  skillTypes?: SkillType[];
  exerciseFormats?: ExerciseFormat[];
  parent: BaseIdWrapper;
  questionDetails: QuestionDetail[];
};

export type QuestionWithChildren = Question & {
  children: QuestionWithChildren[];
};

export type ExamPartQuestion = BaseResponse & {
  mark: number;
  weight: number;
  examPartQuestionIndex: number;
  question: Question;
};

export type Exam = NameCodeResponse & {
  type: ExamType;
  approvalStatus: ApprovalStatus;
  priority: number;
  requiredTicketCount: number;
  questionCount: number;
  duration: number;
  schoolYear: SchoolYear;
  subject: Subject;
  status: ExamResponseStatus;
  lastExamShiftId?: string;
};

export type ExamPart = NameCodeResponse & {
  description: string;
  examPartIndex: number;
  examPartQuestions: ExamPartQuestion[];
  children: ExamPart[];
};

export type ExerciseFormat = NameCodeResponse & {
  theoryLink: string;
  lectureLink: string;
  child: boolean;
  subject: Subject;
  grade: Grade;
  [key: string]: any;
};

export type ExamTemplate = NameCodeResponse & {
  defaultDuration: number;
  schoolYear: SchoolYear;
  subject: Subject;
  examTemplateParts: ExamTemplatePart[];
};

export type ExamTemplatePart = NameCodeResponse & {
  description: string;
  partIndex: number;
  startIndex: number;
  numberOfQuestions: number;
  defaultQuestionType: QuestionType;
  defaultMark: number;
  defaultWeight: number;
  examTemplateParts: ExamTemplatePart[];
};

export type Tag = BaseResponse & {
  id: string;
  name: string;
  description?: string;
};

export type Category = BaseResponse & {
  id: string;
  name: string;
  description?: string;
};

export type LearningBook = BaseResponse & {
  id: string;
  name: string;
  description?: string;
  schoolLevel?: string;
  group?: string;
  grade?: string;
  status?: LearningBookStatus;
};

export type LearningAsset = BaseResponse & {
  id: string;
  name: string;
  description?: string;
  originFilename: string;
  filePath: string;
  thumbnailPath?: string;
  learningBook: BaseIdWrapper;
  chapter?: string;
  lesson?: string;
  status?: LearningAssetStatus;
  tags: BaseIdWrapper[];
  categories: BaseIdWrapper[];
};

export interface AuthenticationResponse {
  accessToken: string;
  refreshToken: string;
}

export type Theory = NameCodeResponse & {
  url: string;
  description: string;
  thumbnails: string;
  status: LectureStatus;
  type: LectureType;
  subject: Subject;
  grade: Grade;
  audioPath?: any;
  contents?: Content[];
};

export type Lecture = NameCodeResponse & {
  url: string;
  description: string;
  thumbnails: string;
  status: LectureStatus;
  type: LectureType;
  subject: Subject;
  grade: Grade;
  audioPath?: any;
  contents?: Content[];
};

export type FormatContent = 'AUDIO' | 'VIDEO' | 'TEXT';

export enum FormatContentEnum {
  AUDIO = 'AUDIO',
  VIDEO = 'VIDEO',
  TEXT = 'TEXT',
}

export type Content = BaseResponse & {
  [key: string]: any;
  parentId?: string;
  format: FormatContent;
  url?: string;
  contentOrder?: number;
  audioPath?: string;
};

export type Game = NameCodeResponse & {
  url: string;
  description: string;
  thumbnails: string;
  status: GameStatus;
  type: GameType;
};

export type Colaborator = NameCodeResponse & {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  role?: string;
  imageUrl?: string;
};

export type ActiveCode = BaseResponse & {
  code: string;
  book?: Book;
  status: string;
  activeDate: string;
  userId: string;
  serialNumber: string;
  isbn: string;
};

export type CommonInfoStat = {
  totalUser?: number;
  totalExam?: number;
  totalQnaTicket?: number;
  totalPractice?: number;
};

export type UserRegisterStat = {
  createdDate?: string;
  userCount?: number;
  extraInfo?: string;
};

export type QnaTicketCountStat = {
  status?: string;
  ticketCount?: number;
};

export type ExamPracticeDoneStat = {
  month?: string;
  examCompletedCount?: number;
  practiceCompletedCount?: number;
  description?: string;
};


export type FeatureResponse = BaseResponse & {
  name: string;
  description: string;
  url: string;
  params: number;
  featureCode: FeatureCode;
}

export type PlanFeatureResponse = BaseResponse & {
  feature: BaseIdWrapper;
  orderNumber: number;
}

export type PlanResponse = BaseResponse & {
  name: string;
  price: number;
  pointFree: number;
  pointPremium: number;
  iconUrl: string;
  isContact: boolean | null | undefined;
  pointFreeEnable: boolean | null | undefined;
  pointPremiumEnable: boolean | null | undefined;
  productType: ProductType;
  features: PlanFeatureResponse[];
  orderNumber: number;
  isPopular: boolean | null | undefined;
}

export type UserSubscriptionResponse = BaseResponse & {
  pointFree: number;
  pointPremium: number;
  subscriptionDate: string;
  expirationDate: string;
  userId: String;
  plan: BaseIdWrapper;
  pointFreeEnable: boolean | null | undefined;
  pointPremiumEnable: boolean | null | undefined;
  isEnable: boolean | null | undefined;
}

export interface OrderResponse {
  id: string
  totalPrice: number
  discountPrice: number
  status: OrderState
  userId: string
  firstName: string
  lastName: string
  customerName: string
  companyName: string
  taxCode: string
  country: string
  address: string
  postalCode: string
  province: string
  email: string
  phone: string
  extraInfo: string
  discountCode: string
  orderCode: string
  payment: PaymentResponse
  orderDetails: OrderDetailResponse[]
  createdAt: string
  createdBy: string
  lastModifiedAt: string
  lastModifiedBy: string
  webViewActive: boolean
  webViewOrder: number
}

export interface PaymentResponse {
  id: string
  paymentChannel: PaymentChannel
  amount: number
  userId: string
  paymentDate: string
  createdAt: string
  createdBy: string
  lastModifiedAt: string
  lastModifiedBy: string
  webViewActive: boolean
  webViewOrder: number
}

export interface OrderDetailResponse {
  id: string
  productQuantity: number
  price: number
  pointQuantity: number
  productName: string
  productId: string
  productType: ProductType
  note: string
  createdAt: string
  createdBy: string
  lastModifiedAt: string
  lastModifiedBy: string
  webViewActive: boolean
  webViewOrder: number
}